import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatRippleModule } from '@angular/material/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

import { FastLaneWizardService } from '../../../services/fastlane-wizard.service';
import { FastLaneWizardStepperService } from '../../../services/fastlane-wizard-stepper.service';
import { FastLaneWizardStep } from '../../../models/fastlane-wizard-state';

enum StepStatus {
	WIP = 0,
	WAIT = 1,
	DONE = 2,
}

interface FastLaneWizardStepObject {
	num: number;
	labelTransId: string;
	status: StepStatus;
}

@Component({
	selector: 'copo-fastlane-wizard-stepper',
	templateUrl: './wizard-stepper.component.html',
	styleUrl: './wizard-stepper.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgClass, NgFor, MatRippleModule, TranslateModule, NgIf, MatIconModule, MatButtonModule]
})
export class WizardStepperComponent implements OnDestroy {
	@Input() currentStep = 0;

	readonly WIP = StepStatus.WIP;
	readonly WAIT = StepStatus.WAIT;
	readonly DONE = StepStatus.DONE;

	steps: FastLaneWizardStepObject[] = [
		{
			num: 0,
			labelTransId: 'fastlane.wizard.stepper.step.main.data',
			status: StepStatus.WIP
		},
		{
			num: 1,
			labelTransId: 'fastlane.wizard.stepper.step.items.details',
			status: StepStatus.WAIT
		},
		{
			num: 2,
			labelTransId: 'fastlane.wizard.stepper.step.transport.details',
			status: StepStatus.WAIT
		},
		{
			num: 3,
			labelTransId: 'fastlane.wizard.stepper.step.transport.summary',
			status: StepStatus.DONE
		}
	];

	private readonly stepChangeSupscription: Subscription;

	constructor(
		private readonly fastlaneStepperService: FastLaneWizardStepperService,
		private readonly fastlaneWizardService: FastLaneWizardService,
	) {
		this.stepChangeSupscription = this.fastlaneStepperService.onStepChanged.subscribe({
			next: (result: { step: number }) => {
				this.currentStep = result.step;
				this.rearengeSteps();
			}
		});
	}

	ngOnDestroy() {
		this.stepChangeSupscription.unsubscribe();
	}

	nextStep() {
		this.currentStep += 1;
		this.rearengeSteps();
	}

	async onStepClick(stepClick: number) {
		await this.fastlaneWizardService.actionMoveUsingSteps(stepClick);
	}

	isSummaryStep() {
		return this.fastlaneWizardService.getState().stepIndex === FastLaneWizardStep.SUMMARY;
	}

	print() {
		print();
	}

	private rearengeSteps() {
		this.processCurrentStep();
		this.processPriviousStep();
		this.processLeftSteps();
	}

	private processCurrentStep() {
		const currentStep = this.steps[this.currentStep];
		currentStep.status = StepStatus.WIP;
		this.steps[this.currentStep] = currentStep;
	}

	private processPriviousStep() {
		if (this.currentStep <= 0) {
			return;
		}
		for (let i = this.currentStep - 1; i >= 0; i--) {
			const previousStep = this.steps[i];
			previousStep.status = StepStatus.DONE;
			this.steps[i] = previousStep;
		}
	}

	private processLeftSteps() {
		if (this.currentStep === this.steps.length - 1) {
			return;
		}
		for (let i = this.currentStep + 1; i < this.steps.length; i++) {
			const leftStep = this.steps[i];
			leftStep.status = StepStatus.WAIT;
			this.steps[i] = leftStep;
		}
	}
}
